// src/pages/notifications/NotificationsPage.jsx - Refactored main container
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Hooks
import { useNotificationsManagement } from '../hooks/useNotificationsManagement';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';

// Components
import NotificationsHeader from '../components/NotificationsHeader';
import NotificationsStats from '../components/NotificationsStats';
import NotificationsFilters from '../components/NotificationsFilters';
import NotificationsBulkActions from '../components/NotificationsBulkActions';
import NotificationsActionsBar from '../components/NotificationsActionsBar';
import NotificationsList from '../components/NotificationsList';
import NotificationActionMenu from '../components/NotificationActionMenu';
import NotificationsMobileFab from '../components/NotificationsMobileFab';

export default function NotificationsPage() {
  const { t } = useTranslation(['notifications', 'common']);
  const navigate = useNavigate();
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();

  // Filter and UI state
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterRead, setFilterRead] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Action menu state
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [currentNotificationForAction, setCurrentNotificationForAction] = useState(null);

  // API hook
  const {
    notifications = [],
    stats = null,
    loading = false,
    error = null,
    unreadCount = 0,
    hasMore = false,
    fetchNotifications = () => {},
    loadMoreNotifications = () => {},
    markAsRead = () => {},
    markMultipleAsRead = () => {},
    markAllAsRead = () => {},
    deleteNotification = () => {},
    deleteMultipleNotifications = () => {},
    fetchStats = () => {},
    clearError = () => {},
  } = useNotificationsManagement() || {};

  // Load initial data
  useEffect(() => {
    fetchNotifications();
    fetchStats();
  }, []);

  // Filtered notifications
  const filteredNotifications = useMemo(() => {
    if (!Array.isArray(notifications)) return [];
    
    let filtered = [...notifications];

    if (filterType !== 'all') {
      filtered = filtered.filter(n => n.notification_type === filterType);
    }

    if (filterPriority !== 'all') {
      filtered = filtered.filter(n => n.priority === filterPriority);
    }

    if (filterRead === 'unread') {
      filtered = filtered.filter(n => !n.is_read);
    } else if (filterRead === 'read') {
      filtered = filtered.filter(n => n.is_read);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(n => 
        n.title?.toLowerCase().includes(query) ||
        n.message?.toLowerCase().includes(query)
      );
    }

    return filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [notifications, filterType, filterPriority, filterRead, searchQuery]);

  // Computed values
  const activeFiltersCount = [filterType, filterPriority, filterRead].filter(f => f !== 'all').length;

  // Event handlers
  const handleRefresh = () => {
    fetchNotifications();
    fetchStats();
  };

  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleClearFilters = () => {
    setFilterType('all');
    setFilterPriority('all');
    setFilterRead('all');
    setSearchQuery('');
  };

  const handleNotificationSelect = (notificationId) => {
    setSelectedNotifications(prev => {
      const isSelected = prev.includes(notificationId);
      if (isSelected) {
        return prev.filter(id => id !== notificationId);
      } else {
        return [...prev, notificationId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedNotifications.length === filteredNotifications.length) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(filteredNotifications.map(n => n.notification_id));
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.is_read && markAsRead && typeof markAsRead === 'function') {
        await markAsRead(notification.notification_id);
      }

      if (notification.onclick_url) {
        navigate(notification.onclick_url);
      }
    } catch (error) {
      console.error('Error handling notification click:', error);
    }
  };

  const handleNotificationAction = (notification, event) => {
    event.stopPropagation();
    setCurrentNotificationForAction(notification);
    setActionMenuAnchor(event.currentTarget);
  };

  const handleCloseActionMenu = () => {
    setActionMenuAnchor(null);
    setCurrentNotificationForAction(null);
  };

  const handleMarkAsRead = async () => {
    if (!currentNotificationForAction || currentNotificationForAction.is_read) return;

    try {
      const result = await markAsRead(currentNotificationForAction.notification_id);
      if (result?.success) {
        showSuccess && showSuccess(t('notifications:page_messages.marked_read'));
      } else {
        showError && showError(t('notifications:page_messages.mark_read_failed'));
      }
    } catch (error) {
      console.error('Error marking as read:', error);
      showError && showError(t('notifications:page_messages.mark_read_failed'));
    }
  };

  const handleDeleteNotification = () => {
    if (!currentNotificationForAction) return;

    if (showConfirmDialog && typeof showConfirmDialog === 'function') {
      showConfirmDialog({
        title: t('notifications:page_messages.delete_title'),
        message: t('notifications:page_messages.delete_confirm'),
        confirmText: t('common:buttons.delete'),
        severity: 'error',
        onConfirm: async () => {
          try {
            const result = await deleteNotification(currentNotificationForAction.notification_id);
            if (result?.success) {
              showSuccess && showSuccess(t('notifications:page_messages.deleted'));
            } else {
              showError && showError(t('notifications:page_messages.delete_failed'));
            }
          } catch (error) {
            console.error('Error deleting notification:', error);
            showError && showError(t('notifications:page_messages.delete_failed'));
          }
        },
      });
    }
  };

  const handleMarkSelectedAsRead = async () => {
    if (selectedNotifications.length === 0) return;

    try {
      const result = await markMultipleAsRead(selectedNotifications);
      if (result?.success) {
        showSuccess && showSuccess(t('notifications:page_messages.marked_many_read', { count: result.successCount }));
        setSelectedNotifications([]);
      } else {
        showError && showError(t('notifications:page_messages.mark_read_failed'));
      }
    } catch (error) {
      console.error('Error marking multiple as read:', error);
      showError && showError(t('notifications:page_messages.mark_read_failed'));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedNotifications.length === 0) return;

    if (showConfirmDialog && typeof showConfirmDialog === 'function') {
      showConfirmDialog({
        title: t('notifications:page_messages.delete_many_title'),
        message: t('notifications:page_messages.delete_many_confirm', { count: selectedNotifications.length }),
        confirmText: t('common:buttons.delete'),
        severity: 'error',
        onConfirm: async () => {
          try {
            const result = await deleteMultipleNotifications(selectedNotifications);
            if (result?.success) {
              showSuccess && showSuccess(t('notifications:page_messages.deleted_many', { count: result.successCount }));
              setSelectedNotifications([]);
            } else {
              showError && showError(t('notifications:page_messages.delete_failed'));
            }
          } catch (error) {
            console.error('Error deleting multiple notifications:', error);
            showError && showError(t('notifications:page_messages.delete_failed'));
          }
        },
      });
    }
  };

  const handleClearSelection = () => {
    setSelectedNotifications([]);
  };

  const handleMarkAllAsRead = async () => {
    try {
      const result = await markAllAsRead();
      if (result?.success) {
        showSuccess && showSuccess(t('notifications:page_messages.marked_many_read', { count: result.count || unreadCount }));
      } else {
        showError && showError(t('notifications:page_messages.mark_read_failed'));
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
      showError && showError(t('notifications:page_messages.mark_read_failed'));
    }
  };

  return (
    <Box>
      {/* Header */}
      <NotificationsHeader
        onRefresh={handleRefresh}
        onToggleFilters={handleToggleFilters}
        loading={loading}
        activeFiltersCount={activeFiltersCount}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* Quick Stats */}
      <NotificationsStats stats={stats} />

      {/* Filters */}
      <NotificationsFilters
        show={showFilters}
        searchQuery={searchQuery}
        filterType={filterType}
        filterPriority={filterPriority}
        filterRead={filterRead}
        onSearchChange={setSearchQuery}
        onTypeChange={setFilterType}
        onPriorityChange={setFilterPriority}
        onReadStatusChange={setFilterRead}
        onClearFilters={handleClearFilters}
      />

      {/* Bulk Actions */}
      <NotificationsBulkActions
        selectedCount={selectedNotifications.length}
        onMarkAsRead={handleMarkSelectedAsRead}
        onDelete={handleDeleteSelected}
        onClearSelection={handleClearSelection}
      />

      {/* Actions Bar */}
      <NotificationsActionsBar
        selectedCount={selectedNotifications.length}
        totalFilteredCount={filteredNotifications.length}
        totalCount={notifications.length}
        unreadCount={unreadCount}
        onSelectAll={handleSelectAll}
        onMarkAllAsRead={handleMarkAllAsRead}
      />

      {/* Notifications List */}
      <NotificationsList
        notifications={filteredNotifications}
        totalNotifications={notifications.length}
        selectedNotifications={selectedNotifications}
        loading={loading}
        hasMore={hasMore}
        onNotificationSelect={handleNotificationSelect}
        onNotificationClick={handleNotificationClick}
        onNotificationAction={handleNotificationAction}
        onLoadMore={loadMoreNotifications}
      />

      {/* Action Menu */}
      <NotificationActionMenu
        anchorEl={actionMenuAnchor}
        notification={currentNotificationForAction}
        onClose={handleCloseActionMenu}
        onMarkAsRead={handleMarkAsRead}
        onDelete={handleDeleteNotification}
      />

      {/* Mobile FAB */}
      <NotificationsMobileFab />
      <ConfirmDialog />
    </Box>
  );
}